import { useEffect, useRef } from "react";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css"; // Import Quill styles
import { Delta } from "quill/core";
import { QuillService } from "../../../../core/service/QuillService";

function preventToolbarFocusLoss(event: Event) {
  event.preventDefault();
}

interface QuillEditorProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
}

export default function QuillEditor({
  value,
  onChange,
  readOnly,
}: QuillEditorProps) {
  const { quill, quillRef } = useQuill(QuillService.config);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Charger le contenu initial dans Quill
  useEffect(() => {
    if (!quill) return;

    // against bug with ol and li
    quill.clipboard.dangerouslyPasteHTML(
      QuillService.restoreQuillHtml(value ?? "")
    );

    const handleTextChange = (
      _delta: Delta,
      _oldDelta: Delta,
      source: string
    ) => {
      if (source !== "user") return;
      const delta: Delta = quill.getContents();
      const html = QuillService.quillDeltaToHtml(delta);
      const cleanHtml = QuillService.cleanHTML(html);

      onChangeRef.current(cleanHtml);
    };

    quill.on("text-change", handleTextChange);

    const toolbar = quill.getModule("toolbar") as
      | { container?: HTMLElement }
      | undefined;
    toolbar?.container?.addEventListener("mousedown", preventToolbarFocusLoss);

    quill.enable(!readOnly);

    // clean Html
    quill.clipboard.addMatcher(Node.TEXT_NODE, function (node: Node) {
      return new Delta().insert(
        QuillService.cleanHTML(node.textContent ?? ""),
        {
          background: null,
          color: null,
          fontWeight: null,
          fontStyle: null,
          fontSize: null,
          header: null,
          textDecoration: null,
        }
      );
    });

    return () => {
      quill.off("text-change", handleTextChange);
      toolbar?.container?.removeEventListener(
        "mousedown",
        preventToolbarFocusLoss
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quill, readOnly]);

  return (
    <div
      className={`bg-white rounded-md dark:bg-gray-500 border border-gray-300 dark:border-gray-600 ${
        readOnly ? "hide-toolbar" : ""
      }`}
    >
      <div ref={quillRef} />
    </div>
  );
}
